/**
 * AWS Lambda Service
 *
 * Utility functions for invoking AWS Lambda functions directly from the frontend
 * with proper CORS handling and Cognito authentication
 */
import { fetchAuthSession } from "aws-amplify/auth";

/**
 * Invoke a Lambda function through API Gateway with proper CORS handling
 * and optional authentication
 *
 * @param functionPath The path to the Lambda function in API Gateway
 * @param payload The payload to send to the Lambda function
 * @param method The HTTP method to use (default: POST)
 * @param requiresAuth Whether the endpoint requires authentication (default: false)
 * @returns The response from the Lambda function
 */
const invokeLambdaFunction = async (
  functionPath: string,
  payload: Record<string, any>,
  method: "GET" | "POST" = "POST",
  requiresAuth: boolean = false
) => {
  try {
    // console.log(`Invoking Lambda function: ${functionPath}`, payload);

    // Create a URL for the Lambda function
    const endpoint = `${process.env.REACT_APP_API_GATEWAY_URL}${functionPath}`;

    // Prepare request options with proper CORS headers
    const options: RequestInit = {
      method: method,
      mode: "cors", // Enable CORS
      credentials: "same-origin", // Include credentials if needed
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        // Additional headers that might help with CORS
        "Access-Control-Request-Method": method,
        "Access-Control-Request-Headers": "Content-Type, Accept",
      },
    };

    // Add authentication token if required
    if (requiresAuth) {
      try {
        const session = await fetchAuthSession();
        if (!session || !session.tokens) {
          throw new Error("No active session. Please authenticate first.");
        }

        // Use the ID token for authentication
        const idToken = session.tokens.idToken?.toString();
        if (!idToken) {
          throw new Error("No ID token available. Please authenticate first.");
        }

        (options.headers as Record<string, string>)[
          "Authorization"
        ] = `Bearer ${idToken}`;
        console.log("Added authentication token to request");
      } catch (authError) {
        console.error("Authentication error:", authError);
        throw new Error("Authentication failed. Please sign in again.");
      }
    }

    // Add body for POST requests
    if (method === "POST") {
      options.body = JSON.stringify(payload);
    }

    // For GET requests, append query parameters to the URL
    let requestUrl = endpoint;
    if (method === "GET" && Object.keys(payload).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(payload).forEach(([key, value]) => {
        queryParams.append(key, String(value));
      });
      requestUrl = `${endpoint}?${queryParams.toString()}`;
    }

    console.log(`Making ${method} request to: ${requestUrl}`);
    const response = await fetch(requestUrl, options);

    // Handle non-OK responses
    if (!response.ok) {
      // Add better error handling for auth errors
      if (response.status === 401 || response.status === 403) {
        throw new Error(`Authentication error: ${response.status}`);
      }

      // Try to get error details from response
      let errorMessage = "";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || JSON.stringify(errorData);
      } catch (e) {
        // If we can't parse JSON, use status text
        errorMessage = response.statusText;
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    console.log(`Lambda function ${functionPath} response:`, data);
    return data;
  } catch (error) {
    // Add specific handling for auth errors
    if (error instanceof Error) {
      if (error.message.includes("Authentication error")) {
        console.error("Authentication failed:", error);
        throw new Error(`Authentication failed: ${error.message}`);
      } else if (error.message.includes("NetworkError")) {
        console.error(
          `CORS error invoking Lambda function ${functionPath}. Check API Gateway CORS configuration.`
        );
        throw new Error(
          `CORS error: Unable to access the API. This may be due to CORS configuration issues.`
        );
      }
    }

    console.error(`Error invoking Lambda function ${functionPath}:`, error);
    throw error;
  }
};

/**
 * Verifies a registration code
 *
 * @param registrationCode The individual registration code to verify
 * @returns Response with valid flag and email if valid
 */
export interface VerifyRegistrationCodeOutput {
  /**
   * Whether the registration code is valid
   */
  email?: string;
  valid: boolean;
  /**
   * Error message if verification fails
   */
  error?: string;
}

export const verifyRegistrationCode = async (
  registrationCode: string
): Promise<VerifyRegistrationCodeOutput> => {
  try {
    const functionName = process.env.REACT_APP_VERIFY_SCHOOL_CODE_FUNCTION;
    if (!functionName) {
      throw new Error(
        "VERIFY_SCHOOL_CODE_FUNCTION environment variable is not set"
      );
    }

    // Use the correct plural path as mentioned in the implementation guide
    const functionPath = "registration/verify";

    // Based on the Lambda function implementation, it expects the registration code
    // in the request body, not as a query parameter. So we should use POST method.
    return await invokeLambdaFunction(
      functionPath,
      { registrationCode },
      "POST"
    );
  } catch (error) {
    console.error("Error verifying registration code:", error);

    // Provide a more user-friendly error message for CORS issues
    if (error instanceof Error && error.message.includes("CORS")) {
      return {
        valid: false,
        error:
          "Unable to verify registration code due to network restrictions. Please try again later.",
      };
    }

    return { valid: false, error: (error as Error).message };
  }
};

/**
 * Enroll student to an exam
 * Requires Cognito authentication - userId is extracted from auth token
 *
 * @param examId The ID of the exam to enroll in
 * @returns Response with enrollment details
 */

export const enrollExam = async (examId: string) => {
  try {
    const functionName = process.env.REACT_APP_ENROLL_EXAM_FUNCTION;
    if (!functionName) {
      throw new Error(
        "REACT_APP_ENROLL_EXAM_FUNCTION environment variable is not set"
      );
    }

    // Use the correct path format for enrollment
    const functionPath = `exams/${examId}/enroll`;

    // This endpoint requires authentication - user ID will be extracted from the auth token
    return await invokeLambdaFunction(
      functionPath,
      { examId }, // Only need examId in the request body, userId comes from token
      "POST",
      true // Requires authentication - important!
    );
  } catch (error) {
    console.error("Error verifying school code:", error);

    // Provide a more user-friendly error message for CORS issues
    if (error instanceof Error && error.message.includes("CORS")) {
      return {
        valid: false,
        error:
          "Unable to verify school code due to network restrictions. Please try again later.",
      };
    }

    return { valid: false, error: (error as Error).message };
  }
};

/**
 * Test function to verify CORS is properly configured
 * This function can be called from the browser console to test if CORS is working
 *
 * @example
 * // Run in browser console
 * import { testCorsConfiguration } from 'aws/lambda';
 * testCorsConfiguration();
 */
export const testCorsConfiguration = async (): Promise<void> => {
  const endpoints = [
    "registration/verify", // Correct path (plural)
    "registration/verify", // Alternative path (singular)
  ];

  const baseUrl = process.env.REACT_APP_API_GATEWAY_URL;
  const testCode = "TEST123";

  console.log("==== CORS CONFIGURATION TEST ====");
  console.log(`Using API Gateway URL: ${baseUrl}`);

  for (const endpoint of endpoints) {
    try {
      console.log(`Testing endpoint: ${endpoint}`);

      // Test with POST method (primary method based on Lambda implementation)
      console.log(`  Testing POST request...`);
      const postResult = await invokeLambdaFunction(
        endpoint,
        { registrationCode: testCode },
        "POST"
      );
      console.log(
        `  ✅ POST Success! Endpoint ${endpoint} responded with:`,
        postResult
      );

      // Also test with GET method for completeness
      try {
        console.log(`  Testing GET request...`);
        const getResult = await invokeLambdaFunction(
          endpoint,
          { registrationCode: testCode },
          "GET"
        );
        console.log(
          `  ✅ GET Success! Endpoint ${endpoint} responded with:`,
          getResult
        );
      } catch (getError) {
        console.error(`  ❌ GET Error with endpoint ${endpoint}:`, getError);
        console.log(
          `  Note: GET failure is expected if the Lambda only accepts POST requests`
        );
      }
    } catch (error) {
      console.error(`  ❌ POST Error with endpoint ${endpoint}:`, error);
    }
  }

  console.log("==== CORS TEST COMPLETE ====");
};

/**
 * Registers a new user with the given information
 *
 * @param userData User registration data including schoolCode and cognitoUserId
 * @returns Response with registration status
 */
/**
 * Verify a user's account through Lambda
 * This step is called after confirmSignUp and before signIn
 *
 * @param username Username to verify
 * @returns Verification result
 */
/**
 * Gets the total count of registered users
 * This endpoint does not require authentication
 *
 * @returns Response with the total user count
 */
export const getUserCount = async () => {
  try {
    const functionPath = "users/count";
    return await invokeLambdaFunction(
      functionPath,
      {}, // No payload needed
      "GET",
      false // No authentication required
    );
  } catch (error) {
    console.error("Error getting user count:", error);
    // Return a default value instead of throwing
    return {
      count: 0,
      error: "Unable to fetch user count",
    };
  }
};

export const verifyUser = async (username: string) => {
  try {
    const functionPath = "users/verify";
    return await invokeLambdaFunction(
      functionPath,
      { username },
      "POST",
      false // No authentication required
    );
  } catch (error) {
    console.error("Error verifying user:", error);

    // If user is already confirmed, treat as success
    if (
      error instanceof Error &&
      error.message.includes(
        "User cannot be confirmed. Current status is CONFIRMED"
      )
    ) {
      return {
        valid: true,
        message: "User is already confirmed",
      };
    }

    // Handle other errors
    if (error instanceof Error && error.message.includes("CORS")) {
      return {
        valid: false,
        error:
          "Unable to verify user due to network restrictions. Please try again later.",
      };
    }

    return { valid: false, error: (error as Error).message };
  }
};

/**
 * Register a user with the backend after Cognito registration
 * This connects the Cognito user with the existing user record
 *
 * @param userData Object containing cognitoUserId (and optionally schoolCode for backward compatibility)
 * @returns Response with registration status
 */
export const registerUser = async (userData: {
  cognitoUserId: string;
  schoolCode?: string;
}) => {
  try {
    const functionName = process.env.REACT_APP_REGISTER_USER_FUNCTION;
    if (!functionName) {
      throw new Error("REGISTER_USER_FUNCTION environment variable is not set");
    }

    // Use the specified path for the register user lambda
    const functionPath = "registration/connect";

    // Use POST method (default) for registration
    // Note: This endpoint does not require authentication as it's used during signup
    return await invokeLambdaFunction(functionPath, userData, "POST", false);
  } catch (error) {
    console.error("Error registering user:", error);

    // Handle specific error cases from the new Lambda implementation
    if (error instanceof Error) {
      // User not found error (404)
      if (error.message.includes("No user found with this email address")) {
        throw new Error(
          "No user found with your email address. Please contact an administrator to create your account."
        );
      }

      // CORS-related errors
      if (error.message.includes("CORS")) {
        throw new Error(
          "Unable to register user due to network restrictions. Please try again later."
        );
      }
    }

    throw error;
  }
};

/**
 * Submits an exam and gets a presigned URL for file upload
 * Requires Cognito authentication
 *
 * @param examId The ID of the exam to submit
 * @returns Response with presigned URL for file upload
 */
export const submitExam = async (examId: string) => {
  try {
    console.log(`Submitting exam: ${examId}`);

    // This endpoint requires authentication
    const functionPath = `exams/${examId}/submissions`;

    const result = await invokeLambdaFunction(
      functionPath,
      {}, // No additional payload needed; examId is in path and studentId comes from token
      "POST",
      true // Requires authentication
    );

    console.log("Exam submission result:", result);
    return result;
  } catch (error) {
    console.error("Error submitting exam:", error);

    if (error instanceof Error && error.message.includes("Authentication")) {
      throw new Error(
        "Authentication required to submit exam. Please sign in."
      );
    }

    throw error;
  }
};

/**
 * Gets submission details for an exam, including download URL if available
 * Requires Cognito authentication
 *
 * @param examId The ID of the exam to get submission for
 * @returns Response with submission details and download URL
 */

export const getSubmission = async (examId: string) => {
  try {
    // console.log(`Getting submission for exam: ${examId}`);

    // This endpoint requires authentication
    const functionPath = `exams/${examId}/submissions`;

    const result = await invokeLambdaFunction(
      functionPath,
      {}, // No additional payload needed; examId is in path and studentId comes from token
      "GET",
      true // Requires authentication
    );

    // console.log("Get submission result:", result);
    return result;
  } catch (error) {
    console.error("Error getting submission:", error);

    if (error instanceof Error && error.message.includes("Authentication")) {
      throw new Error(
        "Authentication required to get submission. Please sign in."
      );
    }

    throw error;
  }
};

/**
 * Gets user information including purchased and enrolled exam quantities
 * Requires Cognito authentication
 *
 * @returns Response with user information including:
 * - cognitoUsername: The username of the user
 * - purchasedHLQuantity: Number of HL exams purchased
 * - purchasedSLQuantity: Number of SL exams purchased
 * - enrolledHLQuantity: Number of HL exams already enrolled in
 * - enrolledSLQuantity: Number of SL exams already enrolled in
 * - updatedAt: Timestamp of last update
 */
export const getUserInfo = async () => {
  try {
    console.log("Getting user information...");
    // This endpoint requires authentication
    const functionPath = "users/info";

    const result = await invokeLambdaFunction(
      functionPath,
      {}, // No additional payload needed
      "GET",
      true // Requires authentication
    );

    console.log("Get user info result:", result);
    return result;
  } catch (error) {
    console.error("Error getting user information:", error);

    if (error instanceof Error && error.message.includes("Authentication")) {
      throw new Error(
        "Authentication required to get user information. Please sign in."
      );
    }

    throw error;
  }
};

/**
 * Gets the user's enrolled exams
 * Requires Cognito authentication
 *
 * @returns Response with enrolled exams data including:
 * - userId: The ID of the user
 * - enrolledExams: Array of exam enrollment records
 * - enrolledHLQuantity: Count of enrolled HL exams
 * - enrolledSLQuantity: Count of enrolled SL exams
 */
export const getEnrolledExams = async () => {
  try {
    console.log("Getting enrolled exams...");
    // This endpoint requires authentication
    const functionPath = "exams/enrolled";

    const result = await invokeLambdaFunction(
      functionPath,
      {}, // No additional payload needed
      "GET",
      true // Requires authentication
    );

    console.log("Get enrolled exams result:", result);
    return result;
  } catch (error) {
    console.error("Error getting enrolled exams:", error);

    if (error instanceof Error && error.message.includes("Authentication")) {
      throw new Error(
        "Authentication required to get enrolled exams. Please sign in."
      );
    }

    throw error;
  }
};

/**
 * Gets bundle information for an exam
 * Requires Cognito authentication
 *
 * @param examId The ID of the exam to get bundle information for
 * @returns Response with bundle information including bundle level, required quota, and other exams
 */
export const getExamBundle = async (examId: string) => {
  try {
    console.log(`Getting bundle information for exam: ${examId}`);
    // This endpoint requires authentication
    const functionPath = `exams/${examId}/bundle`;

    const result = await invokeLambdaFunction(
      functionPath,
      {}, // No additional payload needed
      "GET",
      true // Requires authentication
    );

    console.log("Get exam bundle result:", result);
    return result;
  } catch (error) {
    console.error("Error getting exam bundle information:", error);

    if (error instanceof Error && error.message.includes("Authentication")) {
      throw new Error(
        "Authentication required to get bundle information. Please sign in."
      );
    }

    // If the exam is not part of a bundle, the API might return a 404
    // We'll handle this gracefully by returning null
    if (error instanceof Error && error.message.includes("404")) {
      return null;
    }

    throw error;
  }
};
